// Supplier Register page — rollup-enriched register of suppliers.
//
// Above the controls sits a three-stat summary strip (active suppliers,
// avg contracts/supplier, supplier concentration) gated on the list's
// `complete` flag; an incomplete page shows a subtle partial-view banner
// and still renders the data. The register reads rollup columns from the
// server-computed `supplier.rollup` — contracts (active of total),
// committed value, a committed-value trend sparkline, a value-weighted
// grade roll-up (GradeLetter compact), and status — with a 3-way
// sortable header (asc → desc → none) on name / contracts / committed
// value / grade / status (null-grade rows pinned last in both
// directions). A Table↔Cards segmented control persists the chosen view
// to localStorage. Search matches name / ABN / any contact name from
// `supplier.contacts[]` (the flat contactName/contactPhone fields are no
// longer read here). Row names link to the supplier detail page where
// editing now lives; the form modal is mounted only for creating a new
// supplier.

import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router";
import {
  Plus,
  Search,
  Building2,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Info,
  LayoutGrid,
  Table as TableIcon,
} from "lucide-react";
import {
  Button,
  LoadingSpinner,
  Alert,
  Badge,
  StatBlock,
  Sparkline,
} from "../../components/common";
import { GradeLetter } from "../../components/manage/contract-overview/GradeLetter";
import SupplierFormModal from "../../components/manage/SupplierFormModal";
import {
  useSuppliers,
  useCreateSupplier,
} from "../../hooks/useSuppliers";
import { formatAud0dp } from "../../utils/formatCurrency";
import { useAuth } from "../../contexts/AuthContext";
import {
  GRADE_SEVERITY,
  SUPPLIER_GRADE_HELP,
  gradeResultForRollup,
  supplierGradeWord,
  type GradeResult,
} from "../../lib/contract-overview/contractGrade";
import type {
  SupplierStatus,
  CreateSupplierInput,
  SupplierWithRollup,
  SupplierRollup,
  SupplierGradeRollup,
} from "../../types";

// A rollup that successfully computed (status 'complete' or 'partial').
// On these the counts and committed value are guaranteed numbers — null
// counts/value occur only on 'unavailable' (per the SupplierRollup type
// contract). committedValueByQuarter / gradeRollup stay nullable because
// 'partial' degrades those specifically. Narrowing here lets the metric
// cells render without nullish fallbacks that could never fire.
type AvailableRollup = SupplierRollup & {
  activeContracts: number;
  totalContracts: number;
  committedValue: number;
};

function isAvailable(rollup: SupplierRollup): rollup is AvailableRollup {
  return rollup.status !== "unavailable";
}

type SortKey = "name" | "contracts" | "value" | "grade" | "status";
type SortDir = "asc" | "desc" | "none";
type ViewMode = "table" | "cards";

const VIEW_STORAGE_KEY = "cg-sup-view";
// Null/awaiting grade sorts after every lettered grade in both directions.
const AWAITING_SEVERITY = Number.POSITIVE_INFINITY;

// Format an ABN as `NN NNN NNN NNN` per AU convention. Falls back to
// the raw value when the input isn't 11 digits — supplier ABNs ought
// to be validated upstream, but the formatter shouldn't crash on
// legacy data.
function formatAbn(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 11) return raw;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
}

// Local alias keeps call-sites readable while delegating to the shared
// adapter in contractGrade so both supplier surfaces stay structurally
// in lockstep with a single source of truth.
const gradeResultFor = (rollup: SupplierGradeRollup | null): GradeResult =>
  gradeResultForRollup(rollup);

function severityFor(supplier: SupplierWithRollup): number {
  const letter = supplier.rollup.gradeRollup?.letter ?? null;
  return letter ? GRADE_SEVERITY[letter] : AWAITING_SEVERITY;
}

// Per-column comparator (ascending intent). Null numeric metrics sort
// last so sparse rows never crowd the top of the ascending order.
function compareByKey(
  key: SortKey,
  a: SupplierWithRollup,
  b: SupplierWithRollup,
): number {
  switch (key) {
    case "name":
      return a.name.localeCompare(b.name, "en-AU", { sensitivity: "base" });
    case "contracts": {
      const ca = a.rollup.activeContracts ?? Number.POSITIVE_INFINITY;
      const cb = b.rollup.activeContracts ?? Number.POSITIVE_INFINITY;
      return ca - cb;
    }
    case "value": {
      const va = a.rollup.committedValue ?? Number.POSITIVE_INFINITY;
      const vb = b.rollup.committedValue ?? Number.POSITIVE_INFINITY;
      return va - vb;
    }
    case "grade": {
      const sa = severityFor(a);
      const sb = severityFor(b);
      // Short-circuit when both are equal (including two null-grade rows
      // where both are +Infinity) to avoid Infinity - Infinity = NaN,
      // which produces an implementation-defined sort order.
      if (sa === sb) return 0;
      return sa - sb;
    }
    case "status":
      return a.status.localeCompare(b.status, "en-AU");
  }
}

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | null;
  currentDir: SortDir;
  onClick: () => void;
  align?: "left" | "right" | "center";
  testId?: string;
  children?: React.ReactNode;
}

function SortableHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onClick,
  align = "left",
  testId,
  children,
}: SortableHeaderProps) {
  const isActive = currentKey === sortKey && currentDir !== "none";
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? currentDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const thAlign =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";
  return (
    <th
      className={`${thAlign} px-4 py-3 text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider`}
      aria-sort={ariaSort}
    >
      <span
        className={`inline-flex items-center gap-1 ${align === "center" ? "justify-center" : align === "right" ? "justify-end" : ""}`}
      >
        <button
          type="button"
          onClick={onClick}
          data-testid={testId}
          aria-label={`Sort by ${label}`}
          className={`group inline-flex items-center gap-1 text-caption font-medium uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-200 ${
            isActive
              ? "text-brand-600 dark:text-brand-400"
              : "text-slate-500 dark:text-slate-400"
          }`}
        >
          {label}
          {!isActive && (
            <ChevronsUpDown
              className="h-3.5 w-3.5 opacity-25 group-hover:opacity-60"
              aria-hidden="true"
            />
          )}
          {isActive && currentDir === "asc" && (
            <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {isActive && currentDir === "desc" && (
            <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
        {children}
      </span>
    </th>
  );
}

function readStoredView(): ViewMode {
  if (typeof window === "undefined") return "table";
  return window.localStorage.getItem(VIEW_STORAGE_KEY) === "cards"
    ? "cards"
    : "table";
}

export default function SupplierRegister() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sort, setSort] = useState<{ key: SortKey | null; dir: SortDir }>({
    key: null,
    dir: "none",
  });
  const [view, setView] = useState<ViewMode>(readStoredView);
  const [showGradeHelp, setShowGradeHelp] = useState(false);

  const cycleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "none" };
    });
  };

  // Dismiss the grade-help popover on Escape, matching the app's
  // other click-toggle info-panel patterns (e.g. MeetingSummaryStrip).
  useEffect(() => {
    if (!showGradeHelp) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowGradeHelp(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showGradeHelp]);

  const changeView = (next: ViewMode) => {
    setView(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    }
  };

  const [showModal, setShowModal] = useState(false);

  const { data, isLoading, error } = useSuppliers();
  const createMutation = useCreateSupplier();

  const canCreate =
    user?.role === "admin" ||
    user?.role === "editor" ||
    user?.role === "global-admin";

  const summary = data?.summary;
  const isComplete = data?.complete !== false;

  const filteredSuppliers = useMemo(() => {
    const q = search.toLowerCase();
    let items = (data?.items ?? []).filter((s) => {
      const matchesSearch =
        !search ||
        s.name.toLowerCase().includes(q) ||
        s.abn.includes(search) ||
        (s.contacts ?? []).some((c) =>
          (c.name ?? "").toLowerCase().includes(q),
        );
      const matchesStatus = statusFilter === "all" || s.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    if (sort.key && sort.dir !== "none") {
      const direction = sort.dir === "asc" ? 1 : -1;
      const key = sort.key;
      items = items.slice().sort((a, b) => {
        // Null numeric metrics (unavailable rollups) and null-grade rows
        // always anchor to the bottom regardless of sort direction, so
        // the useful data stays visible at the top.
        const raw = compareByKey(key, a, b);
        // compareByKey uses +Infinity as the null sentinel. A positive
        // sentinel difference (a is null, b is not) means a sinks last in
        // BOTH directions; vice-versa for b null.
        if (raw === Number.POSITIVE_INFINITY) return 1;
        if (raw === Number.NEGATIVE_INFINITY) return -1;
        return raw * direction;
      });
    }
    return items;
  }, [data?.items, search, statusFilter, sort.key, sort.dir]);

  const handleSubmit = async (
    formData: CreateSupplierInput & { status?: SupplierStatus },
  ) => {
    await createMutation.mutateAsync(formData);
    setShowModal(false);
  };

  const statusBadge = (status: SupplierStatus) =>
    status === "active" ? (
      <Badge variant="active" label="Active" />
    ) : (
      <Badge variant="archived" label="Inactive" />
    );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">
          Supplier Register
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Manage your organisation&apos;s suppliers.
        </p>
      </div>

      {summary && (
        <div
          data-testid="supplier-summary-strip"
          className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        >
          <StatBlock
            label="Engaged suppliers"
            value={summary.activeSuppliers}
            sub="engaged with ≥1 active contract"
          />
          <StatBlock
            label="Avg contracts/supplier"
            value={summary.avgContractsPerSupplier.toFixed(1)}
            sub="active contracts ÷ engaged suppliers"
          />
          <StatBlock
            label="Supplier concentration"
            value={
              summary.concentration ? `${summary.concentration.percent}%` : "—"
            }
            sub={
              summary.concentration
                ? summary.concentration.sub
                : "no committed value yet"
            }
          />
        </div>
      )}

      {!isComplete && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-body-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
          Showing a partial view — refine filters to see complete totals.
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            aria-label="Search suppliers"
            placeholder="Search by name, ABN, or contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9"
          />
        </div>
        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label="Choose layout"
            className="inline-flex rounded-md border border-slate-200 bg-slate-100 p-0.5 dark:border-slate-700 dark:bg-slate-800"
          >
            {(
              [
                { value: "table" as const, label: "Table", Icon: TableIcon },
                { value: "cards" as const, label: "Cards", Icon: LayoutGrid },
              ]
            ).map(({ value, label, Icon }) => {
              const active = view === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => changeView(value)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1 text-caption font-medium transition-colors ${
                    active
                      ? "bg-white text-slate-900 shadow-soft-sm dark:bg-slate-700 dark:text-slate-50"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {label}
                </button>
              );
            })}
          </div>
          {canCreate && (
            <Button
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setShowModal(true)}
            >
              New Supplier
            </Button>
          )}
        </div>
      </div>

      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Filter by status"
      >
        <span className="text-caption text-slate-500 dark:text-slate-400">
          Status:
        </span>
        {(
          [
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ] as const
        ).map((opt) => {
          const active = statusFilter === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              aria-pressed={active}
              className={`rounded-md px-2.5 py-1 text-caption font-medium transition-colors ${
                active
                  ? "bg-brand-600 text-white dark:bg-brand-500"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {error && <Alert variant="error">{(error as Error).message}</Alert>}

      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      )}

      {!isLoading && filteredSuppliers.length === 0 && (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="h-12 w-12 text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-body text-slate-500 dark:text-slate-400">
            No suppliers found.
          </p>
        </div>
      )}

      {!isLoading && filteredSuppliers.length > 0 && view === "table" && (
        <SupplierTable
          suppliers={filteredSuppliers}
          sort={sort}
          cycleSort={cycleSort}
          statusBadge={statusBadge}
          showGradeHelp={showGradeHelp}
          onToggleGradeHelp={() => setShowGradeHelp((v) => !v)}
        />
      )}

      {!isLoading && filteredSuppliers.length > 0 && view === "cards" && (
        <SupplierCards suppliers={filteredSuppliers} statusBadge={statusBadge} />
      )}

      <SupplierFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        supplier={undefined}
        isLoading={createMutation.isPending}
      />
    </div>
  );
}

interface RollupViewProps {
  suppliers: SupplierWithRollup[];
  statusBadge: (status: SupplierStatus) => React.ReactNode;
}

// Committed-value trend: the aria-hidden sparkline always travels with
// an inline (sr-only) value counterpart carrying the latest quarter, so
// the trend is never conveyed by the decorative chart alone. A degraded
// rollup (partial — no quarter series) shows an honest em-dash instead.
function TrendCell({ series }: { series: number[] | null }) {
  if (!series || series.length === 0) {
    return <span className="text-slate-400 dark:text-slate-500">—</span>;
  }
  const latest = series[series.length - 1];
  return (
    <span
      className="inline-flex items-center gap-2"
      data-testid="supplier-trend-sparkline"
    >
      <Sparkline
        data={series}
        className="text-brand-500 dark:text-brand-400"
      />
      <span className="sr-only">
        Latest quarter committed value {formatAud0dp(latest)}
      </span>
    </span>
  );
}

function SupplierTable({
  suppliers,
  sort,
  cycleSort,
  statusBadge,
  showGradeHelp,
  onToggleGradeHelp,
}: RollupViewProps & {
  sort: { key: SortKey | null; dir: SortDir };
  cycleSort: (key: SortKey) => void;
  showGradeHelp: boolean;
  onToggleGradeHelp: () => void;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <SortableHeader
                label="Name"
                sortKey="name"
                currentKey={sort.key}
                currentDir={sort.dir}
                onClick={() => cycleSort("name")}
                testId="name-sort-toggle"
              />
              <SortableHeader
                label="Contracts"
                sortKey="contracts"
                currentKey={sort.key}
                currentDir={sort.dir}
                onClick={() => cycleSort("contracts")}
                testId="contracts-sort-toggle"
              />
              <SortableHeader
                label="Committed value"
                sortKey="value"
                currentKey={sort.key}
                currentDir={sort.dir}
                onClick={() => cycleSort("value")}
                align="right"
                testId="value-sort-toggle"
              />
              <th className="text-left px-4 py-3 text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Trend
              </th>
              <SortableHeader
                label="Grade"
                sortKey="grade"
                currentKey={sort.key}
                currentDir={sort.dir}
                onClick={() => cycleSort("grade")}
                align="center"
                testId="grade-sort-toggle"
              >
                <span className="relative inline-flex">
                  <button
                    type="button"
                    onClick={onToggleGradeHelp}
                    aria-label="How the grade is computed"
                    aria-expanded={showGradeHelp}
                    className="inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  {showGradeHelp && (
                    <span
                      role="tooltip"
                      className="absolute right-0 top-6 z-10 w-64 rounded-md border border-slate-200 bg-white p-3 text-left text-caption font-normal normal-case tracking-normal text-slate-600 shadow-soft-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:shadow-soft-lg-dark"
                    >
                      {SUPPLIER_GRADE_HELP}
                    </span>
                  )}
                </span>
              </SortableHeader>
              <SortableHeader
                label="Status"
                sortKey="status"
                currentKey={sort.key}
                currentDir={sort.dir}
                onClick={() => cycleSort("status")}
                testId="status-sort-toggle"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {suppliers.map((supplier) => {
              const { rollup } = supplier;
              return (
                <tr
                  key={supplier.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/manage/suppliers/${supplier.id}`}
                      data-testid="supplier-name-cell"
                      className="text-body font-medium text-slate-900 dark:text-slate-50 hover:text-brand-600 dark:hover:text-brand-400"
                    >
                      {supplier.name}
                    </Link>
                    <div className="font-mono text-caption text-slate-500 dark:text-slate-400">
                      {formatAbn(supplier.abn)}
                    </div>
                  </td>
                  {isAvailable(rollup) ? (
                    <>
                      <td className="px-4 py-3 text-body-sm text-slate-600 dark:text-slate-400 tabular-nums">
                        {rollup.activeContracts} of {rollup.totalContracts}
                      </td>
                      <td className="px-4 py-3 text-body-sm text-right tabular-nums text-slate-700 dark:text-slate-300">
                        {formatAud0dp(rollup.committedValue)}
                      </td>
                      <td className="px-4 py-3">
                        <TrendCell series={rollup.committedValueByQuarter} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <GradeLetter
                          result={gradeResultFor(rollup.gradeRollup)}
                          variant="compact"
                          label={supplierGradeWord(rollup.gradeRollup?.letter)}
                        />
                      </td>
                    </>
                  ) : (
                    <td
                      colSpan={4}
                      className="px-4 py-3 text-body-sm italic text-slate-400 dark:text-slate-500"
                    >
                      Unavailable — rollup couldn&apos;t be computed
                    </td>
                  )}
                  <td className="px-4 py-3">{statusBadge(supplier.status)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupplierCards({ suppliers, statusBadge }: RollupViewProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {suppliers.map((supplier) => {
        const { rollup } = supplier;
        return (
          <div
            key={supplier.id}
            className="card flex flex-col gap-3 p-5"
            data-testid="supplier-card"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link
                  to={`/manage/suppliers/${supplier.id}`}
                  data-testid="supplier-name-cell"
                  className="text-h4 text-slate-900 dark:text-slate-50 hover:text-brand-600 dark:hover:text-brand-400"
                >
                  {supplier.name}
                </Link>
                <div className="font-mono text-caption text-slate-500 dark:text-slate-400">
                  {formatAbn(supplier.abn)}
                </div>
              </div>
              {statusBadge(supplier.status)}
            </div>

            {isAvailable(rollup) ? (
              <>
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <p className="text-overline uppercase text-slate-500 dark:text-slate-400">
                      Committed value
                    </p>
                    <p className="text-h3 tabular-nums text-slate-900 dark:text-slate-50">
                      {formatAud0dp(rollup.committedValue)}
                    </p>
                  </div>
                  <TrendCell series={rollup.committedValueByQuarter} />
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3 dark:border-slate-700">
                  <div className="text-body-sm text-slate-600 dark:text-slate-400">
                    <span className="tabular-nums">
                      {rollup.activeContracts} of {rollup.totalContracts}
                    </span>{" "}
                    contracts
                    {rollup.nextExpiry && (
                      <div className="text-caption text-slate-500 dark:text-slate-400">
                        Next expiry {rollup.nextExpiry.contractTitle}
                      </div>
                    )}
                  </div>
                  <GradeLetter
                    result={gradeResultFor(rollup.gradeRollup)}
                    variant="compact"
                    label={supplierGradeWord(rollup.gradeRollup?.letter)}
                  />
                </div>
              </>
            ) : (
              <p className="text-body-sm italic text-slate-400 dark:text-slate-500">
                Unavailable — rollup couldn&apos;t be computed
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
